/**
 * Resolve selected-tap destination without requiring TemplateDocuments.
 * Prefer frozen item.selectedTapAction; fall back to legacy template doc; then auto.
 */
import type { PublicShareManifest, SnapshotItem } from '@containd/shared';
import {
  parseLinkTarget,
  resolveWebSharePolicies,
} from '@containd/shared';
import { shouldUseGenericCanvasItem } from '@containd/template-render';

export type SelectedTapDestination = 'view' | 'open' | 'none';

type ElementInteractions = {
  selectedTapAction?: 'auto' | 'view' | 'open';
};

function elementInteractionsFromTemplate(
  manifest: PublicShareManifest,
  item: SnapshotItem,
): ElementInteractions | undefined {
  const doc = manifest.templateDocuments?.[item.templateId] as
    | {
        elements?: Array<{
          id?: string;
          interactions?: ElementInteractions;
        }>;
      }
    | undefined;
  if (!doc?.elements?.length) return undefined;
  const el =
    doc.elements.find((e) => e.id === item.templateElementId) ?? doc.elements[0];
  return el?.interactions;
}

function hasNestedItems(
  manifest: PublicShareManifest,
  itemUuid: string,
): boolean {
  if (manifest.items.some((i) => i.parentUuid === itemUuid)) return true;
  return (manifest.canvases ?? []).some(
    (c) => c.parentUuid === itemUuid && (c.itemUuids?.length ?? 0) > 0,
  );
}

function hasCanvasSvgBackground(
  manifest: PublicShareManifest,
  item: SnapshotItem,
): boolean {
  const graphics = item.svgGraphicsMediaId?.trim();
  if (graphics) return true;
  return (manifest.canvases ?? []).some((c) => {
    if (c.parentUuid !== item.uuid) return false;
    return Boolean(c.svgBackgroundMediaId?.trim());
  });
}

/**
 * Enterable when nested items exist or the interior has an SVG canvas background.
 * `isContainer` alone is not enough (empty containers should not be entered).
 */
export function canEnterShareItem(
  manifest: PublicShareManifest,
  item: SnapshotItem,
): boolean {
  return hasNestedItems(manifest, item.uuid) || hasCanvasSvgBackground(manifest, item);
}

/** `auto` (default): enter if nestable, else view. Honors webSharePolicies.openEmptyViewer. */
export function resolveSelectedTapDestination(
  manifest: PublicShareManifest,
  item: SnapshotItem,
): SelectedTapDestination {
  const frozen = item.selectedTapAction;
  const fromTemplate = elementInteractionsFromTemplate(manifest, item)
    ?.selectedTapAction;
  const action = frozen ?? fromTemplate ?? 'auto';
  let destination: 'view' | 'open';
  if (action === 'view') destination = 'view';
  else if (action === 'open') destination = 'open';
  else destination = canEnterShareItem(manifest, item) ? 'open' : 'view';

  const policies = resolveWebSharePolicies(item.webSharePolicies);
  if (destination === 'view' && !policies.openEmptyViewer) {
    return 'none';
  }
  return destination;
}

/**
 * Host parity (`shouldOpenCanvasTitleLink` + activate): when not entering a
 * nest, a valid item.linkOut opens instead of empty viewer / no-op.
 */
export function shouldOpenLinkOutOnActivate(
  destination: SelectedTapDestination,
  linkOut: string | null | undefined,
): boolean {
  if (destination === 'open') return false;
  return parseLinkTarget(linkOut) != null;
}

export function isGenericSnapshotItem(item: SnapshotItem): boolean {
  return shouldUseGenericCanvasItem(item.templateId, item.templateElementId);
}

export function shouldShowSelectionHandles(options: {
  selected: boolean;
  isShareRoot: boolean;
  isPlaceholder: boolean;
  openOnTap: boolean;
  destination: SelectedTapDestination;
}): boolean {
  if (!options.selected) return false;
  if (options.isShareRoot || options.isPlaceholder || options.openOnTap) {
    return false;
  }
  return options.destination !== 'none';
}

export function shouldShowEnterHandle(
  showHandles: boolean,
  canEnter: boolean,
): boolean {
  return showHandles && canEnter;
}

export function shouldShowLayoutControls(options: {
  selected: boolean;
  generic: boolean;
  layoutTotal: number;
  allowLayoutChange: boolean;
  openOnTap: boolean;
}): boolean {
  return (
    options.selected &&
    !options.generic &&
    options.layoutTotal > 1 &&
    options.allowLayoutChange &&
    !options.openOnTap
  );
}

/**
 * Builtin layout flag — only consulted for legacy templateDocuments.
 * Host freeze should set publishedLayouts / bakedDescriptors instead.
 */
export function activeLayoutIsBuiltin(
  manifest: PublicShareManifest,
  item: SnapshotItem,
  layoutIndex: number,
): boolean {
  const doc = manifest.templateDocuments?.[item.templateId] as
    | {
        elements?: Array<{
          id?: string;
          layouts?: Array<{ renderAsBuiltin?: boolean }>;
        }>;
      }
    | undefined;
  if (!doc?.elements?.length) return false;
  const el =
    doc.elements.find((e) => e.id === item.templateElementId) ?? doc.elements[0];
  const layout = el?.layouts?.[layoutIndex];
  return layout?.renderAsBuiltin === true;
}
